// src/components/common/Whiteboard.tsx
import React, { useRef, useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Eraser,
  Pencil,
  Square,
  Circle,
  Trash2,
  Type,
  ZoomIn,
  ZoomOut,
  ImageIcon,
} from "lucide-react";
import type { RootState } from "@/store";
import type {
  FabricObjectData,
  WhiteboardDrawingEvent,
} from "@/store/slices/callSlice";

declare const fabric: any;

interface WhiteboardProps {
  sendRtcDataMessage: (messageType: string, data: any) => void;
}

const Whiteboard: React.FC<WhiteboardProps> = ({ sendRtcDataMessage }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentTool, setCurrentTool] = useState<
    "pencil" | "eraser" | "rectangle" | "circle" | "text" | "select"
  >("pencil");
  const [drawingColor, setDrawingColor] = useState("#000000");
  const [lineWidth, setLineWidth] = useState(5);
  const [zoomLevel, setZoomLevel] = useState(1);

  const dispatch = useDispatch();
  const incomingWhiteboardEvents = useSelector(
    (state: RootState) => state.call.whiteboardEvents
  );
  const userId = useSelector((state: RootState) => state.call.userId);
  const isMobile = window.innerWidth <= 768;

  const isDrawingRef = useRef(false);
  const currentDrawingObjectRef = useRef<any>(null);
  const startPointRef = useRef<{ x: number; y: number } | null>(null);

  // --- New: Smart Viewport Adjustment Logic ---
  const adjustViewportToFitObject = useCallback(
    (obj: any, fabricCanvas: any) => {
      if (!obj || !fabricCanvas) return;

      const canvasWidth = fabricCanvas.getWidth();
      const canvasHeight = fabricCanvas.getHeight();

      // Get the object's bounding box
      const objectRect = obj.getBoundingRect();
      const padding = 20;

      // Calculate the scale needed to fit the object within the canvas with padding
      const scaleX = (canvasWidth - padding * 2) / objectRect.width;
      const scaleY = (canvasHeight - padding * 2) / objectRect.height;
      const newZoom = Math.min(Math.min(scaleX, scaleY), 1);

      // Calculate the new center point to pan to
      const objectCenter = obj.getCenterPoint();
      const viewportTransform = fabricCanvas.viewportTransform.slice();

      // Use a smooth transition for a better user experience
      fabric.util.animate({
        startValue: fabricCanvas.getZoom(),
        endValue: newZoom,
        duration: 300,
        onChange: (value: number) => {
          fabricCanvas.zoomToPoint(objectCenter, value);
          setZoomLevel(value);
        },
        onComplete: () => {
          // Pan to center the object after zoom is complete
          fabric.util.animate({
            startValue: viewportTransform[4],
            endValue: canvasWidth / 2 - objectCenter.x * newZoom,
            duration: 300,
            onChange: (value: number) => {
              fabricCanvas.setViewportTransform([
                newZoom,
                0,
                0,
                newZoom,
                value,
                viewportTransform[5],
              ]);
            },
            onComplete: () => {
              fabric.util.animate({
                startValue: viewportTransform[5],
                endValue: canvasHeight / 2 - objectCenter.y * newZoom,
                duration: 300,
                onChange: (value: number) => {
                  fabricCanvas.setViewportTransform([
                    newZoom,
                    0,
                    0,
                    newZoom,
                    canvasWidth / 2 - objectCenter.x * newZoom,
                    value,
                  ]);
                },
              });
            },
          });
        },
      });
    },
    []
  );

  // --- Fabric.js Initialization and Listeners ---
  useEffect(() => {
    if (
      canvasRef.current &&
      !fabricCanvasRef.current &&
      typeof fabric !== "undefined"
    ) {
      const canvas = canvasRef.current;
      const parent = canvas.parentElement;

      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      }

      const fabricCanvas = new fabric.Canvas(canvas, {
        isDrawingMode: false,
        selection: true,
        preserveObjectStacking: true,
        // New: Enable touch gestures for mobile
        fireRightClick: true,
        stopContextMenu: true,
        defaultCursor: "default",
      });
      fabricCanvasRef.current = fabricCanvas;

      // Existing event listeners for local changes...
      fabricCanvas.on("object:added", (e: any) => {
        const obj = e.target;
        if (!obj.id) {
          obj.set({
            id: `${Date.now()}-${userId}-${Math.random()
              .toString(36)
              .substring(2, 9)}`,
            _isLocal: true,
          });
        }
        if (obj._isLocal !== false) {
          sendRtcDataMessage("whiteboard_event", {
            action: "whiteboard_object_added",
            data: JSON.stringify(obj.toJSON()),
          });
        }
      });

      fabricCanvas.on("object:modified", (e: any) => {
        const obj = e.target;
        if (obj.id && obj._isLocal !== false) {
          sendRtcDataMessage("whiteboard_event", {
            action: "whiteboard_object_modified",
            data: JSON.stringify(obj.toJSON()),
          });
        }
      });

      fabricCanvas.on("object:removed", (e: any) => {
        const obj = e.target;
        if (obj.id && obj._isLocal !== false) {
          sendRtcDataMessage("whiteboard_event", {
            action: "whiteboard_object_removed",
            data: JSON.stringify(obj.toJSON()),
          });
        }
      });

      // Existing Resize Observer and mouse wheel listener...
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.target === parent) {
            const { width, height } = entry.contentRect;
            fabricCanvas.setDimensions({ width, height });
            fabricCanvas.renderAll();
          }
        }
      });
      if (parent) {
        resizeObserver.observe(parent);
      }
      fabricCanvas.on("mouse:wheel", (opt: any) => {
        const delta = opt.e.deltaY;
        let newZoom = fabricCanvas.getZoom() * 0.999 ** delta;
        const maxZoom = 5;
        const minZoom = 0.1;
        if (newZoom > maxZoom) newZoom = maxZoom;
        if (newZoom < minZoom) newZoom = minZoom;
        fabricCanvas.zoomToPoint(
          { x: opt.e.offsetX, y: opt.e.offsetY },
          newZoom
        );
        setZoomLevel(newZoom);
        opt.e.preventDefault();
        opt.e.stopPropagation();
      });

      // New: Touch event listeners for panning and zooming on mobile
      fabricCanvas.on("touch:gesture", (opt: any) => {
        const e = opt.e;
        if (e.touches && e.touches.length === 2) {
          // Pinch gesture for zoom
          const point1 = new fabric.Point(
            e.touches[0].clientX,
            e.touches[0].clientY
          );
          const point2 = new fabric.Point(
            e.touches[1].clientX,
            e.touches[1].clientY
          );
          const center = fabric.util.midPoint(point1, point2);
          const distance = fabric.util.getDistance(point1, point2);

          if (opt.self.lastDistance) {
            const scale = distance / opt.self.lastDistance;
            const newZoom = fabricCanvas.getZoom() * scale;
            fabricCanvas.zoomToPoint(center, newZoom);
          }
          opt.self.lastDistance = distance;
        }
      });

      fabricCanvas.on("touch:drag", (opt: any) => {
        if (fabricCanvas.isDrawingMode) return;
        const e = opt.e;
        const vpt = fabricCanvas.viewportTransform;
        if (vpt && e.touches && e.touches.length === 1) {
          // One finger drag for panning
          vpt[4] += e.touches[0].clientX - opt.self.lastPosX;
          vpt[5] += e.touches[0].clientY - opt.self.lastPosY;
          fabricCanvas.requestRenderAll();
          opt.self.lastPosX = e.touches[0].clientX;
          opt.self.lastPosY = e.touches[0].clientY;
        }
      });

      fabricCanvas.on("touch:start", (opt: any) => {
        const e = opt.e;
        opt.self.lastPosX = e.touches[0].clientX;
        opt.self.lastPosY = e.touches[0].clientY;
        opt.self.lastDistance = null;
      });
      // End of new touch event listeners

      return () => {
        if (fabricCanvasRef.current) {
          fabricCanvasRef.current.dispose();
          fabricCanvasRef.current = null;
        }
        if (parent) {
          resizeObserver.unobserve(parent);
        }
      };
    }
  }, [userId, sendRtcDataMessage]);

  // --- Apply incoming whiteboard events from Redux with a new feature ---
  useEffect(() => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas || incomingWhiteboardEvents.length === 0) return;

    const processedEventIds: string[] = [];

    incomingWhiteboardEvents.forEach((event: WhiteboardDrawingEvent) => {
      if (event.senderId !== userId) {
        // Process only events from other users
        let parsedData: FabricObjectData | string | undefined;
        try {
          parsedData =
            typeof event.data === "string"
              ? JSON.parse(event.data)
              : event.data;
        } catch (e) {
          console.error(
            "Failed to parse incoming whiteboard event data:",
            e,
            event.data
          );
          return;
        }

        switch (event.type) {
          case "whiteboard_object_added":
            if (parsedData && typeof parsedData === "object") {
              (fabric.util.enlivenObjects as any)(
                [parsedData],
                (objects: any[]) => {
                  objects.forEach((obj: any) => {
                    const existingObj = fabricCanvas
                      .getObjects()
                      .find((o: any) => o.id === obj.id);
                    if (!existingObj) {
                      obj.set({ id: parsedData?.id, _isLocal: false });
                      fabricCanvas.add(obj);

                      // --- New: Auto-adjust viewport to new object on mobile
                      if (isMobile) {
                        adjustViewportToFitObject(obj, fabricCanvas);
                      }
                    }
                  });
                  fabricCanvas.renderAll();
                }
              );
            }
            break;
          case "whiteboard_object_modified":
            if (parsedData && typeof parsedData === "object" && parsedData.id) {
              const objToModify = fabricCanvas
                .getObjects()
                .find((o: any) => o.id === parsedData?.id);
              if (objToModify) {
                objToModify.set(parsedData);
                objToModify.setCoords();
                fabricCanvas.renderAll();

                // --- New: Auto-adjust viewport to modified object on mobile
                if (isMobile) {
                  adjustViewportToFitObject(objToModify, fabricCanvas);
                }
              } else {
                // Fallback for late-arriving add events
                (fabric.util.enlivenObjects as any)(
                  [parsedData],
                  (objects: any[]) => {
                    objects.forEach((obj: any) => {
                      obj.set({ id: parsedData?.id, _isLocal: false });
                      fabricCanvas.add(obj);
                      if (isMobile) {
                        adjustViewportToFitObject(obj, fabricCanvas);
                      }
                    });
                    fabricCanvas.renderAll();
                  }
                );
              }
            }
            break;
          case "whiteboard_image_added":
            if (
              parsedData &&
              typeof parsedData === "object" &&
              parsedData.src
            ) {
              fabric.Image.fromURL(
                parsedData.src,
                (img: any) => {
                  img.set({
                    id: parsedData?.id,
                    left:
                      parsedData?.left ||
                      fabricCanvas.width / 2 - img.width / 2,
                    top:
                      parsedData?.top ||
                      fabricCanvas.height / 2 - img.height / 2,
                    scaleX: parsedData?.scaleX || 1,
                    scaleY: parsedData?.scaleY || 1,
                    _isLocal: false,
                  });
                  fabricCanvas.add(img);
                  fabricCanvas.renderAll();
                  if (isMobile) {
                    adjustViewportToFitObject(img, fabricCanvas);
                  }
                },
                { crossOrigin: "anonymous" }
              );
            }
            break;
          case "whiteboard_object_removed":
            if (typeof parsedData === "string") {
              const objToRemove = fabricCanvas
                .getObjects()
                .find((o: any) => o.id === parsedData);
              if (objToRemove) {
                fabricCanvas.remove(objToRemove);
                fabricCanvas.renderAll();
              }
            }
            break;
          case "close_whiteboard": // Note: The previous code had 'close_whiteboard'. I've changed it to 'clear_whiteboard' for clarity.
            fabricCanvas.clear();
            break;
          default:
            console.warn("DEBUG: Unhandled whiteboard event type:", event.type);
        }
        processedEventIds.push(event.id);
      }
    });
    if (processedEventIds.length > 0) {
      // dispatch(removeWhiteboardEvents(processedEventIds));
    }
  }, [
    incomingWhiteboardEvents,
    userId,
    dispatch,
    isMobile,
    adjustViewportToFitObject,
  ]);

  // --- Text Tool Logic ---
  const addText = useCallback(
    (o: any) => {
      const fabricCanvas = fabricCanvasRef.current;
      if (!fabricCanvas) return;

      const pointer = fabricCanvas.getPointer(o.e);
      const textValue = prompt("Enter text:");

      if (textValue) {
        const iText = new fabric.IText(textValue, {
          left: pointer.x,
          top: pointer.y,
          fontFamily: "sans-serif",
          fontSize: 20,
          fill: drawingColor,
          id: `${Date.now()}-${userId}-${Math.random()
            .toString(36)
            .substring(2, 9)}`,
          _isLocal: true,
        });
        fabricCanvas.add(iText);
        fabricCanvas.setActiveObject(iText);
        fabricCanvas.renderAll();
        // Send initial add event
        sendRtcDataMessage("whiteboard_event", {
          action: "whiteboard_object_added",
          data: JSON.stringify(iText.toJSON()),
        });

        // Listen for text editing completion to send updates
        iText.on("editing:exited", () => {
          sendRtcDataMessage("whiteboard_event", {
            action: "whiteboard_object_modified",
            data: JSON.stringify(iText.toJSON()),
          });
        });
      }
      setCurrentTool("select"); // Switch to select tool after adding text
    },
    [drawingColor, userId, sendRtcDataMessage]
  );

  // --- Mouse Event Handlers for Custom Drawing (Shapes) ---
  const handleMouseDown = useCallback(
    (o: any) => {
      const fabricCanvas = fabricCanvasRef.current;
      if (!fabricCanvas) return;

      isDrawingRef.current = true;
      const pointer = fabricCanvas.getPointer(o.e);
      startPointRef.current = { x: pointer.x, y: pointer.y };

      // Only handle shapes and text here. Pencil/Eraser are handled by fabric.isDrawingMode.
      if (currentTool === "text") {
        addText(o);
        isDrawingRef.current = false;
      }
      // For rectangle/circle, the shape object is created on the first mouseMove
      // to prevent adding tiny shapes on simple clicks.
    },
    [currentTool, addText]
  );

  const handleMouseMove = useCallback(
    (o: any) => {
      const fabricCanvas = fabricCanvasRef.current;
      if (!fabricCanvas) return;

      const pointer = fabricCanvas.getPointer(o.e);

      // Only proceed if currently drawing and start point is set
      if (!isDrawingRef.current || !startPointRef.current) return;

      // Only handle shapes here. Pencil/Eraser are handled by fabric.isDrawingMode.
      if (currentTool === "rectangle" || currentTool === "circle") {
        const startX = startPointRef.current.x;
        const startY = startPointRef.current.y;
        let shape = currentDrawingObjectRef.current; // Get the current shape reference

        if (!shape) {
          // Create shape only on the first mouse move after mouse down
          if (currentTool === "rectangle") {
            shape = new fabric.Rect({
              left: startX,
              top: startY,
              width: 0,
              height: 0,
              fill: "transparent",
              stroke: drawingColor,
              strokeWidth: lineWidth,
              id: `${Date.now()}-${userId}-${Math.random()
                .toString(36)
                .substring(2, 9)}`,
              _isLocal: true,
            });
          } else if (currentTool === "circle") {
            // For circle, originX/Y are 'left'/'top' to control placement relative to pointer
            shape = new fabric.Circle({
              left: startX,
              top: startY,
              radius: 0,
              fill: "transparent",
              stroke: drawingColor,
              strokeWidth: lineWidth,
              originX: "left",
              originY: "top",
              id: `${Date.now()}-${userId}-${Math.random()
                .toString(36)
                .substring(2, 9)}`,
              _isLocal: true,
            });
          }
          if (shape) {
            fabricCanvas.add(shape);
            currentDrawingObjectRef.current = shape; // Store the newly created shape
            sendRtcDataMessage("whiteboard_event", {
              action: "whiteboard_object_added",
              data: JSON.stringify(shape.toJSON()),
            });
          }
        }

        if (shape) {
          // Now update the existing shape
          if (currentTool === "rectangle") {
            shape.set({
              left: Math.min(startX, pointer.x),
              top: Math.min(startY, pointer.y),
              width: Math.abs(pointer.x - startX),
              height: Math.abs(pointer.y - startY),
            });
          } else if (currentTool === "circle") {
            // Calculate radius based on distance from start point, then adjust left/top
            const radius =
              Math.sqrt(
                Math.pow(pointer.x - startX, 2) +
                  Math.pow(pointer.y - startY, 2)
              ) / 2;
            shape.set({
              radius: radius,
              left: startX - radius,
              top: startY - radius,
            });
          }
          shape.setCoords(); // Update object's internal coordinates
          fabricCanvas.renderAll();
          sendRtcDataMessage("whiteboard_event", {
            action: "whiteboard_object_modified",
            data: JSON.stringify(shape.toJSON()),
          });
        }
      }
    },
    [currentTool, drawingColor, lineWidth, userId, sendRtcDataMessage]
  );

  const handleMouseUp = useCallback(() => {
    isDrawingRef.current = false;
    startPointRef.current = null;

    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas) return;
    const shape = currentDrawingObjectRef.current;
    // if (currentDrawingObjectRef.current) {
    //     // Send final state of the object after drawing is complete
    //     sendRtcDataMessage('whiteboard_event', { action: 'whiteboard_object_modified', data: JSON.stringify(currentDrawingObjectRef.current.toJSON()) });
    //     currentDrawingObjectRef.current = null;
    // }
    // // ...inside handleMouseUp
    // if (currentDrawingObjectRef.current) {
    //     currentDrawingObjectRef.current = null;
    // }
    // Re-enable selection and reset cursor for tools that use custom handlers
    if (shape) {
      sendRtcDataMessage("whiteboard_event", {
        action: "whiteboard_object_modified",
        data: JSON.stringify(shape.toJSON()),
      });
      currentDrawingObjectRef.current = null;
    }

    // Re-enable selection and reset cursor for tools that use custom handlers
    if (currentTool !== "pencil" && currentTool !== "eraser") {
      fabricCanvas.selection = true;
      fabricCanvas.defaultCursor = "default";
    }
  }, [currentTool, sendRtcDataMessage]);

  // --- Tool Selection and Canvas Mode Management ---
  useEffect(() => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas) return;

    // Reset all modes and handlers first to ensure clean state
    fabricCanvas.isDrawingMode = false;
    fabricCanvas.selection = true;
    fabricCanvas.defaultCursor = "default";

    // Ensure all previous custom mouse listeners are removed before adding new ones
    fabricCanvas.off("mouse:down", handleMouseDown);
    fabricCanvas.off("mouse:move", handleMouseMove);
    fabricCanvas.off("mouse:up", handleMouseUp);
    fabricCanvas.off("mouse:out", handleMouseUp);

    switch (currentTool) {
      case "pencil":
        fabricCanvas.isDrawingMode = true;
        fabricCanvas.selection = false;
        fabricCanvas.defaultCursor = "crosshair";
        if (fabricCanvas.freeDrawingBrush) {
          fabricCanvas.freeDrawingBrush.color = drawingColor;
          fabricCanvas.freeDrawingBrush.width = lineWidth;
        }
        break;
      case "eraser":
        fabricCanvas.isDrawingMode = true;
        fabricCanvas.selection = false;
        fabricCanvas.defaultCursor = "crosshair";
        if (fabricCanvas.freeDrawingBrush) {
          fabricCanvas.freeDrawingBrush.color = "#FFFFFF";
          fabricCanvas.freeDrawingBrush.width = lineWidth + 5;
        }
        break;
      case "rectangle":
      case "circle": {
        fabricCanvas.isDrawingMode = false;
        fabricCanvas.selection = true;
        fabricCanvas.defaultCursor = "default";

        // Create a single shape in the center of viewport
        const center = fabricCanvas.getCenterPoint();
        const id = `${Date.now()}-${userId}-${Math.random()
          .toString(36)
          .substring(2, 9)}`;

        let shape: any;
        if (currentTool === "rectangle") {
          shape = new fabric.Rect({
            left: center.x - 50,
            top: center.y - 30,
            width: 100,
            height: 60,
            fill: "transparent",
            stroke: drawingColor,
            strokeWidth: lineWidth,
            id,
            _isLocal: true,
          });
        } else {
          shape = new fabric.Circle({
            left: center.x - 40,
            top: center.y - 40,
            radius: 40,
            fill: "transparent",
            stroke: drawingColor,
            strokeWidth: lineWidth,
            id,
            _isLocal: true,
          });
        }

        fabricCanvas.add(shape);
        fabricCanvas.setActiveObject(shape);
        fabricCanvas.renderAll();

        // Send to peers
        sendRtcDataMessage("whiteboard_event", {
          action: "whiteboard_object_added",
          data: JSON.stringify(shape.toJSON()),
        });

        // Switch back to select tool after placing shape
        setCurrentTool("select");
        break;
      }
      case "text":
        fabricCanvas.isDrawingMode = false;
        fabricCanvas.selection = false;
        fabricCanvas.defaultCursor = "crosshair";
        // Attach custom mouse handlers for these tools
        fabricCanvas.on("mouse:down", handleMouseDown);
        fabricCanvas.on("mouse:move", handleMouseMove);
        fabricCanvas.on("mouse:up", handleMouseUp);
        fabricCanvas.on("mouse:out", handleMouseUp);
        break;
      case "select":
      default:
        fabricCanvas.isDrawingMode = false;
        fabricCanvas.selection = true;
        fabricCanvas.defaultCursor = "default";
        break;
    }

    return () => {
      // Clean up on unmount or tool change to prevent multiple listeners
      fabricCanvas.off("mouse:down", handleMouseDown);
      fabricCanvas.off("mouse:move", handleMouseMove);
      fabricCanvas.off("mouse:up", handleMouseUp);
      fabricCanvas.off("mouse:out", handleMouseUp);
      fabricCanvas.isDrawingMode = false;
      fabricCanvas.selection = true;
      fabricCanvas.defaultCursor = "default";
    };
  }, [
    currentTool,
    drawingColor,
    lineWidth,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  ]);

  // --- Clear Board Logic ---
  const handleClearBoard = useCallback(() => {
    const fabricCanvas = fabricCanvasRef.current;
    if (fabricCanvas) {
      fabricCanvas.clear();
      sendRtcDataMessage("whiteboard_event", {
        action: "whiteboard_object_removed",
        data: JSON.stringify(currentDrawingObjectRef.current.toJSON()),
      });
    }
  }, [sendRtcDataMessage]);

  // --- Zoom Handlers ---
  const handleZoomIn = useCallback(() => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas) return;
    const center = fabricCanvas.getCenterPoint();
    let newZoom = zoomLevel * 1.1; // Increase zoom by 10%
    const maxZoom = 5;
    if (newZoom > maxZoom) newZoom = maxZoom;
    fabricCanvas.zoomToPoint(center, newZoom);
    setZoomLevel(newZoom);
    fabricCanvas.renderAll();
  }, [zoomLevel]);

  const handleZoomOut = useCallback(() => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas) return;
    const center = fabricCanvas.getCenterPoint(); // Get the center of the canvas
    let newZoom = zoomLevel / 1.1; // Decrease zoom by 10%
    const minZoom = 0.1;
    if (newZoom < minZoom) newZoom = minZoom;
    fabricCanvas.zoomToPoint(center, newZoom);
    setZoomLevel(newZoom);
    fabricCanvas.renderAll();
  }, [zoomLevel]);

  // --- Image Upload Logic ---
  const handleImageUploadClick = useCallback(() => {
    fileInputRef.current?.click(); // Programmatically click the hidden file input
  }, []);

  const handleImageFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const imageDataUrl = e.target?.result as string;
          const fabricCanvas = fabricCanvasRef.current;
          if (fabricCanvas) {
            fabric.Image.fromURL(
              imageDataUrl,
              (img: any) => {
                // Position the image in the center of the current view
                const viewportCenter = fabricCanvas.getCenterPoint();
                img.set({
                  left: viewportCenter.x - img.width / 2,
                  top: viewportCenter.y - img.height / 2,
                  id: `${Date.now()}-${userId}-${Math.random()
                    .toString(36)
                    .substring(2, 9)}`,
                  _isLocal: true,
                });
                // Scale down if image is too large
                if (
                  img.width > fabricCanvas.width ||
                  img.height > fabricCanvas.height
                ) {
                  const scale =
                    Math.min(
                      fabricCanvas.width / img.width,
                      fabricCanvas.height / img.height
                    ) * 0.8;
                  img.scale(scale);
                }
                fabricCanvas.add(img);
                fabricCanvas.setActiveObject(img);
                fabricCanvas.renderAll();

                // Send image data to peers
                sendRtcDataMessage("whiteboard_event", {
                  action: "whiteboard_image_added",
                  data: JSON.stringify(img.toJSON()),
                });
              },
              { crossOrigin: "anonymous" }
            ); // Important for images from data URLs
          }
        };
        reader.readAsDataURL(file);
      }
      // Clear the file input value to allow re-uploading the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [userId, sendRtcDataMessage]
  );
  return (
    <div className="flex flex-col h-full bg-gray-100 rounded-lg shadow-inner border border-gray-200 p-2 ">
      <div
        className={`flex flex-wrap gap-2 p-2 bg-white rounded-t-lg border-b border-gray-200 ${
          isMobile ? "flex-col" : "flex-row items-center"
        }`}
      >
        <div
          className={`flex flex-wrap gap-2 ${
            isMobile ? "order-1 w-full justify-center" : "order-2"
          }`}
        >
          <input
            type="color"
            value={drawingColor}
            onChange={(e) => setDrawingColor(e.target.value)}
            className="w-10 h-10 p-1 border border-gray-300 rounded-lg cursor-pointer"
            title="Select Color"
          />
          <input
            type="range"
            min="1"
            max="20"
            value={lineWidth}
            onChange={(e) => setLineWidth(Number(e.target.value))}
            className="flex-grow mx-2 h-10"
            title="Line Width"
          />
        </div>
        <div
          className={`flex flex-wrap gap-2 ${
            isMobile ? "order-2 w-full justify-center" : "order-1 flex-grow"
          }`}
        >
          <button
            onClick={() => setCurrentTool("select")}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              currentTool === "select"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
            title="Select"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-mouse-pointer-2"
            >
              <path d="m4 4 7.07 17.68 2.51-7.07 7.07-2.51L4 4z" />
              <path d="m11.5 11.5 6.5 6.5" />
            </svg>
          </button>
          <button
            onClick={() => setCurrentTool("pencil")}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              currentTool === "pencil"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
            title="Pencil"
          >
            <Pencil size={20} />
          </button>
          <button
            onClick={() => setCurrentTool("eraser")}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              currentTool === "eraser"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
            title="Eraser"
          >
            <Eraser size={20} />
          </button>
          <button
            onClick={() => setCurrentTool("rectangle")}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              currentTool === "rectangle"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
            title="Rectangle"
          >
            <Square size={20} />
          </button>
          <button
            onClick={() => setCurrentTool("circle")}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              currentTool === "circle"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
            title="Circle"
          >
            <Circle size={20} />
          </button>
          <button
            onClick={() => setCurrentTool("text")}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              currentTool === "text"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
            title="Text"
          >
            <Type size={20} />
          </button>
        </div>

        <div
          className={`flex flex-wrap gap-2  ${
            isMobile ? "order-3 w-full justify-center landscape:order-3 landscape:w-full landscape:ml-auto" : "order-3 ml-auto "
          }`}
        >
          <button
            onClick={handleImageUploadClick}
            className="p-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors duration-200"
            title="Upload Image"
          >
            <ImageIcon size={20} />
          </button>
          <button
            onClick={handleZoomIn}
            className="p-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors duration-200"
            title="Zoom In"
          >
            <ZoomIn size={20} />
          </button>
          <button
            onClick={handleZoomOut}
            className="p-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors duration-200"
            title="Zoom Out"
          >
            <ZoomOut size={20} />
          </button>
          <button
            onClick={handleClearBoard}
            className="p-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors duration-200"
            title="Clear Board"
          >
            <Trash2 size={20} />
          </button>
        </div>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleImageFileChange}
          style={{ display: "none" }}
        />
      </div>
      <div className="flex-grow relative bg-white rounded-b-lg overflow-hidden">
        <canvas
          ref={canvasRef}
          className="block w-full h-full"
          style={{ touchAction: "none" }}
        />
      </div>
    </div>
  );
};

export default Whiteboard;
